import { Box, Typography, useTheme, Paper } from "@mui/material";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCheckSquare, FiClipboard, FiFile, FiInfo, FiTarget } from "react-icons/fi";
import ResizableTextBox from "../components/input/ResizableTextBox";
import TextBox from "../components/input/TextBox";
import { useContextProvider } from "../components/page-context/ContextProvider";
import FormSelect from "../components/select/FormSelect";
import { Building } from "../db_access/building";
import muiSytle from "../style/components-style/muiStyle";
import { getOrgTokens, ORG_PALETTE } from "../theme/style/organizationPalette";

const BuildingScopeAndGoals = ({ building }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const tok = getOrgTokens(isDark);
    const { isSaveClicked, setIsSaveVisible, setIsSaveClicked, setIsLoading, setRefresh } = useContextProvider();
    const units = [
        "mg", "g", "kg", "ton", "oz", "lb", "kWh", "J", "kJ", "GJ", "Btu", "mmBTU", "t.km", "t.mi", "t.nmi", "s", "min", "h", "day", "wk", "mo", "yr", "mm3", "cm3", "dm3", "m3", "km3", "inch3", "ft3", "L", "gal", "fl_oz", "barrel", "ml", "mm2", "cm2", "dm2", "m2", "km2", "inch2", "ft2", "mm", "cm", "dm", "m", "km", "inch", "ft", "yd", "mi", "nmi", "unit", "piece", "other",
    ];

    const [formData, setFormData] = useState({
        name: "",
        functional_unit: "",
        other_unit: "",
        scope: "",
        goals: "",
        assumptions: "",
        system_boundary: "",
    });

    const [modified, setModified] = useState(false);

    useEffect(() => {
        if (building) {
            setFormData({
                name: building.name || "",
                functional_unit: building.functional_unit || "",
                other_unit: building.other_unit || "",
                scope: building.scope || "",
                goals: building.goals || "",
                assumptions: building.assumptions || "",
                system_boundary: building.system_boundary || "",
            });
        }
        setModified(false);
    }, [building]);

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setFormData({ ...formData, [field]: value });
        setIsSaveVisible(true);
        setModified(true);
    };

    useEffect(() => {
        if (isSaveClicked && modified) {
            setIsLoading(true);
            const fd = new FormData();
            fd.append("name", formData.name || "");
            fd.append("functional_unit", formData.functional_unit || "");
            fd.append("other_unit", formData.other_unit || "");
            fd.append("scope", formData.scope || "");
            fd.append("goals", formData.goals || "");
            fd.append("assumptions", formData.assumptions || "");
            fd.append("system_boundary", formData.system_boundary || "");
            fd.append("id", building.id);

            Building.save_scope_and_goals(fd)
                .then((res) => {
                    if (!res.ok) throw new Error("Failed to update");
                    return res.json();
                })
                .then(() => {
                    setModified(false);
                    setIsSaveVisible(false);
                })
                .catch((err) => {
                    console.error("Error saving building scope and goals:", err);
                    alert("Failed to save. Please try again.");
                })
                .finally(() => {
                    setIsSaveClicked(false);
                    setIsLoading(false);
                    setRefresh((r) => !r);
                });
        }
    }, [isSaveClicked, modified, building?.id, formData]);

    return (
        <Paper variant="Table Container">
            <TableHeaderContainer>
                <TableTitleContainer>
                    <Typography variant="Table Title">{t("Scope and Goals")}</Typography>
                </TableTitleContainer>
            </TableHeaderContainer>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <Box sx={{ display: "flex", gap: 3 }}>
                        <Box style={{ flex: 1 }}>
                            <TextBox Icon={FiFile} label={"Name"} placeholder={"Name"} value={formData.name} onChange={handleChange("name")} />
                        </Box>
                        <Box style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
                            <Box style={{ flex: 1 }}>
                                <FormSelect
                                    Icon={FiClipboard}
                                    label={"Functional Unit"}
                                    placeholder={"Functional Unit"}
                                    options={units}
                                    value={formData.functional_unit}
                                    onChange={handleChange("functional_unit")}
                                />
                            </Box>
                            {formData.functional_unit === "other" && (
                                <Box style={{ flex: 1 }}>
                                    <TextBox Icon={FiFile} label={"Custom Unit"} placeholder={"Specify other functional unit"} value={formData.other_unit} onChange={handleChange("other_unit")} />
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 3 }}>
                        <Box style={{ flex: 1 }}>
                            <ResizableTextBox Icon={FiTarget} label={"Scope"} placeholder={"Scope"} value={formData.scope} onChange={handleChange("scope")} />
                        </Box>
                        <Box style={{ flex: 1 }}>
                            <ResizableTextBox Icon={FiCheckSquare} label={"Goals"} placeholder={"Goals"} value={formData.goals} onChange={handleChange("goals")} />
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Box style={{ flex: 1 }}>
                            <ResizableTextBox
                                Icon={FiInfo}
                                label={"Assumptions"}
                                placeholder={"Assumptions"}
                                value={formData.assumptions}
                                onChange={handleChange("assumptions")}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Box style={{ flex: 1 }}>
                            <ResizableTextBox
                                Icon={FiTarget}
                                label={"System Boundary"}
                                placeholder={"System Boundary"}
                                value={formData.system_boundary}
                                onChange={handleChange("system_boundary")}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

export default BuildingScopeAndGoals;

